import { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components';
import { sendAnalyticsEvents } from '../../analytics/common'
import { EVENTS } from '../../analytics/events'
import { PLANS } from '../../analytics/eCommerce'
import { useAnalyticsData } from '../checkout/helper';
import {
  selectEventsData,
  setAnalyticsData,
  setEventDataList,
} from '../events/eventsSlice'
import {
  selectLandingType,
  selectPlan,
  selectShowCheckout,
  setPlan,
  setShowCheckout
} from './plansSlice'
import { appendLinksList } from '../../helper/utils'
import DefaultPlans from './DefaultPlans'
import TrialPlan from './TrialPlan'
import { fontSize } from '../../mixins'
import UIButton from '../../UI/UIButton'
import ReviewsBlock from './components/ReviewsBlock'
import Guarantee from './components/GuaranteeBlock'
import PressLogos from './components/PressLogos'
import FeaturesList from './components/FeaturesList'

export const TrialHint = styled.div`
  ${fontSize(12)};
  line-height: 16px;
  color: #818181;
  margin-top: 16px;
  transition: opacity 200ms;
  opacity: ${({ show }) => (show ? 1 : 0)};
`;

const FreeTrialWrapper = styled.div`
  display: flex;
  flex-direction: column;
  
  & > * {
    width: 100%;
  }
`;

export const SecondaryTitle = styled.h2`
  font-weight: 700;
  ${fontSize(24)};
  line-height: 29px;
  color: #323232;
  text-align: center;
  letter-spacing: -0.2px;
  margin: ${({customMargin}) => customMargin || 0};
`;

const PlansScreen = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const plan = useSelector(selectPlan);
  const landingType = useSelector(selectLandingType);
  const eventsData = useSelector(selectEventsData);
  const showTrialHint = landingType === 'trial';
  const analyticsParams = useAnalyticsData();
  const showCheckout = useSelector(selectShowCheckout);

  useEffect(() => {dispatch(setAnalyticsData(analyticsParams))},[analyticsParams, dispatch]);

  useEffect(() => {
    appendLinksList('checkout');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[]);

  useEffect(() => {
    !showCheckout &&
      sendAnalyticsEvents(EVENTS.onboardingPaywallShown, eventsData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCheckout]);

  const handleSetPlan = (id) => dispatch(setPlan(id));

  const defaultPlan = <DefaultPlans handleSetPlan={handleSetPlan} />;

  const plansComponents = {
    'fullPrice': defaultPlan,
    'main': defaultPlan,
    'trial': <TrialPlan handleSetPlan={handleSetPlan} />,
  };

  const FEATURES = useMemo(() => {
    return [
      t('know_when_family_come_or_go'),
      t('realtime_location_sharing'),
      t('be_notified_in_case'),
      t('see_the_history'),
      t('notify_your_family'),
    ]
  },[t]);

  const planDetails = useMemo(() => PLANS[plan],[plan]);

  const onContinueClick = () => {
    const { productId, duration, description, periodPrice, price } = planDetails;
    let planData = {
      Value: price,
      value: Math.round(periodPrice * 100),
      currency: 'USD',
      price: periodPrice,
      Duration: duration,
      duration,
      description,
      periodPrice,
      product_id: productId,
    };
    dispatch(setEventDataList(planData));
    sendAnalyticsEvents(EVENTS.onboardingPaywallChosen, {
      ...eventsData,
      ...planData,
    });
    dispatch(setShowCheckout(true))
  };

  const continueButton = (id) => <UIButton onClick={onContinueClick} customId={`paywall-continue-button-${id}`} />;

  const trialHint = planDetails && landingType === 'trial' && (
    <TrialHint show={showTrialHint}>
      {t('trial_hint', {now: planDetails.periodPrice, then: `$${planDetails.price}/ ${planDetails.duration}`})}
    </TrialHint>
  );

  return (
    <>
      {plansComponents[landingType]}
      <FreeTrialWrapper>
        {continueButton(1)}
        <div>
          <SecondaryTitle customMargin={'24px 0 12px'} >{t('what_you_get')}</SecondaryTitle>
          <FeaturesList list={FEATURES} />
        </div>
      </FreeTrialWrapper>
      <Guarantee />
      <SecondaryTitle customMargin={'0 0 20px'}>{t('write_about_us')}</SecondaryTitle>
      <PressLogos />
      <SecondaryTitle customMargin={'0 0 16px'} >{t('customer_reviews')}</SecondaryTitle>
      <ReviewsBlock />
      {continueButton(2)}
      {trialHint}
    </>
  )
};

export default PlansScreen
